import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { LARDER_CATEGORIES } from "./LarderForm";
import { memberApi, errorText } from "../../lib/member";

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

// Review every recipe line before anything is added: edit, untick what you already have, pick a list. Nothing is matched or bought automatically.
export function AddToListDialog({ recipe, recipeType = "heirloom", open, onOpenChange }) {
  const [lists, setLists] = useState(null);
  const [listId, setListId] = useState("");
  const [newName, setNewName] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(newKey);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    setKey(newKey()); setDone(null); setNewName("");
    setRows([...(recipe.ingredients || []), ...(recipe.ingredients_from_method || [])].map(line => ({ include: true, name: line.slice(0, 80), quantity: "", unit: "", category: "Other" })));
    memberApi.get("/lists").then(d => { setLists(d.lists); setListId(d.lists[0]?.id || "new"); }).catch(e => toast.error(errorText(e)));
  }, [open, recipe]);

  const set = (i, k, v) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const chosen = rows.filter(r => r.include && r.name.trim());
  const submit = async (e) => {
    e.preventDefault();
    if (busy || !chosen.length) return;
    setBusy(true);
    try {
      let lid = listId;
      if (lid === "new") lid = (await memberApi.post("/lists", { name: newName.trim() || recipe.title.slice(0, 60), client_key: `${key}-list` })).id;
      const d = await memberApi.post(`/lists/${lid}/from-recipe`, { recipe_id: recipe.id, recipe_type: recipeType, client_key: key,
        items: chosen.map(r => { const q = r.quantity === "" ? null : Number(r.quantity); return { name: r.name.trim(), quantity: Number.isFinite(q) ? q : null, unit: r.unit.trim() || null, category: r.category }; }) });
      setDone({ lid, ...d });
      toast.success(d.duplicate_submit ? "Already added — nothing was duplicated" : `Added ${d.added} item${d.added === 1 ? "" : "s"} to your list`);
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="add-to-list-dialog">
        <DialogHeader>
          <DialogTitle className="fs-serif text-2xl">Add ingredients to a list</DialogTitle>
          <DialogDescription>Review each line from the recipe. Untick anything you already have, adjust amounts, then choose a list. Nothing is ordered or taken out of your larder.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="space-y-4" data-testid="add-to-list-done">
            <p className="text-sm">{done.duplicate_submit ? "These ingredients were already added from this review." : `${done.added} item${done.added === 1 ? "" : "s"} added.`}</p>
            <div className="flex gap-2 justify-end"><button className="fs-btn-2" onClick={() => onOpenChange(false)}>Close</button><Link className="fs-btn" to={`/explore/lists/${done.lid}`} data-testid="add-to-list-open-list">Open the list</Link></div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <label className="block text-xs tracking-widest uppercase text-gray-600">List
              <select className="fs-input mt-1 normal-case tracking-normal" value={listId} onChange={e => setListId(e.target.value)} data-testid="add-to-list-select" disabled={!lists}>
                {(lists || []).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                <option value="new">New list…</option>
              </select>
            </label>
            {listId === "new" && <input className="fs-input" maxLength={60} value={newName} onChange={e => setNewName(e.target.value)} placeholder={recipe.title.slice(0, 60)} aria-label="New list name" data-testid="add-to-list-new-name" />}
            <ul className="space-y-2" data-testid="add-to-list-rows">
              {rows.map((r, i) => (
                <li key={i} className={`grid grid-cols-[auto_1fr] sm:grid-cols-[auto_minmax(0,3fr)_4.5rem_5rem_7.5rem] gap-2 items-center${r.include ? "" : " opacity-50"}`} data-testid={`add-to-list-row-${i}`}>
                  <input type="checkbox" className="w-4 h-4" checked={r.include} onChange={e => set(i, "include", e.target.checked)} aria-label={`Include ${r.name}`} data-testid={`add-to-list-include-${i}`} />
                  <input className="fs-input" maxLength={80} value={r.name} onChange={e => set(i, "name", e.target.value)} aria-label="Item" data-testid={`add-to-list-name-${i}`} />
                  <input className="fs-input col-start-2 sm:col-start-auto" type="number" min="0" step="any" value={r.quantity} onChange={e => set(i, "quantity", e.target.value)} placeholder="Qty" aria-label="Quantity" data-testid={`add-to-list-qty-${i}`} />
                  <input className="fs-input col-start-2 sm:col-start-auto" maxLength={24} value={r.unit} onChange={e => set(i, "unit", e.target.value)} placeholder="Unit" aria-label="Unit" />
                  <select className="fs-input col-start-2 sm:col-start-auto" value={r.category} onChange={e => set(i, "category", e.target.value)} aria-label="Category">{LARDER_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2 justify-between items-center">
              <span className="text-xs text-gray-600" data-testid="add-to-list-count">{chosen.length} of {rows.length} lines selected</span>
              <div className="flex gap-2"><button type="button" className="fs-btn-2" onClick={() => onOpenChange(false)}>Cancel</button>
                <button type="submit" className="fs-btn" disabled={busy || !chosen.length || !lists} data-testid="add-to-list-submit">{busy ? "Adding…" : "Add to list"}</button></div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
